// Servers → ⋯ → Set location: pick the server's country (+ city) by hand, for LAN servers and IPs the
// automatic detection can't place or places wrong. Saved with PUT /api/servers/:id { country_code, city }
// (marked manual, so an IP change won't re-detect over it). "Detect automatically" goes back to the
// IP lookup (not for LAN servers). Country names come from the browser in the panel's language.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';

// ISO 3166-1 alpha-2 (assigned codes)
const CODES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ '
  + 'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE '
  + 'GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP '
  + 'KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF '
  + 'NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN '
  + 'SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT '
  + 'ZA ZM ZW').split(' ');

const regionNames = (lng) => {
  try { return new Intl.DisplayNames([lng, 'en'], { type: 'region' }); } catch (_) { return new Intl.DisplayNames(['en'], { type: 'region' }); }
};

export default function LocationModal({ server, isLan, onClose, onSaved }) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState(server.country_code || '');
  const [city, setCity] = useState(server.city || '');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null); // 'save' | 'detect' | 'clear'
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const countries = useMemo(() => {
    const names = regionNames(i18n.language);
    const en = regionNames('en');
    return CODES.map(c => ({ code: c, name: names.of(c) || c, en: en.of(c) || c }))
      .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [i18n.language]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return countries;
    return countries.filter(c => c.name.toLowerCase().includes(s) || c.en.toLowerCase().includes(s) || c.code.toLowerCase() === s);
  }, [countries, q]);

  // keep the chosen country in view when the dialog opens
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector('.sv-loc-opt.on');
    if (el) el.scrollIntoView({ block: 'center' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const errText = (e) => (e.response && e.response.data && e.response.data.error) || t('servers.locationSaveFailed', 'Could not save the location');
  const save = async () => {
    setBusy('save'); setError('');
    try {
      const c = countries.find(x => x.code === code);
      await axios.put(`/api/servers/${server.id}`, { country_code: code, country_name: c ? c.en : undefined, city: city.trim() });
      onSaved(t('servers.locationSaved', 'Location saved'));
    } catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };
  const clear = async () => {
    setBusy('clear'); setError('');
    try {
      await axios.put(`/api/servers/${server.id}`, { country_code: '', city: '' });
      onSaved(t('servers.locationCleared', 'Location removed'));
    } catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };
  const detect = async () => {
    setBusy('detect'); setError('');
    try {
      const r = await axios.post(`/api/servers/${server.id}/detect-location`);
      if (r.data.success) onSaved(t('servers.locationDetected', 'Location detected: {{city}}, {{country}}', { city: r.data.city, country: r.data.country_name }));
      else setError(t('servers.locationNotFoundPick', 'The location could not be detected from this address. Pick the country below.'));
    } catch (_) { setError(t('servers.locationFailed', 'Failed to detect location')); } finally { setBusy(null); }
  };

  const chosen = countries.find(c => c.code === code);
  const footer = (
    <div className="sv-loc-foot">
      {server.country_code && <button type="button" className="sv-btn danger" onClick={clear} disabled={!!busy}>{t('servers.locationClear', 'Clear')}</button>}
      {!isLan && <button type="button" className="sv-btn" onClick={detect} disabled={!!busy}>{busy === 'detect' ? t('servers.detecting', 'Detecting…') : t('servers.locationDetect', 'Detect automatically')}</button>}
      <span style={{ flex: 1 }} />
      <button type="button" className="sv-btn" onClick={onClose} disabled={!!busy}>{t('common.cancel', 'Cancel')}</button>
      <button type="button" className="sv-btn primary" onClick={save} disabled={!!busy || !code}>{busy === 'save' ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
    </div>
  );

  return (
    <AnimatedModal isOpen onClose={() => { if (!busy) onClose(); }} title={t('servers.setLocationTitle', 'Location of {{name}}', { name: server.name })} footer={footer} maxWidth="460px">
      <div className="sv-locm">
        <p className="sv-loc-hint">
          {isLan
            ? t('servers.locationHintLan', 'A LAN server’s location can’t be detected. Pick where the venue is.')
            : t('servers.locationHint', 'Set by hand when the detected location is missing or wrong. It stays when the IP changes.')}
        </p>
        <div className="sv-loc-preview">
          {chosen ? <img src={`https://flagcdn.com/20x15/${chosen.code.toLowerCase()}.png`} alt="" /> : <span className="sv-loc-noflag" />}
          <span>{chosen ? (city.trim() ? `${city.trim()}, ${chosen.name}` : chosen.name) : t('servers.locationNone', 'No country picked')}</span>
        </div>
        <input className="sv-loc-input" value={q} onChange={e => setQ(e.target.value)} placeholder={t('servers.locationSearch', 'Search countries')} aria-label={t('servers.locationSearch', 'Search countries')} autoFocus />
        <div className="sv-loc-list" ref={listRef} role="listbox" aria-label={t('servers.locationCountry', 'Country')}>
          {shown.length === 0 && <div className="sv-loc-empty">{t('servers.locationNoMatch', 'No country matches “{{q}}”', { q })}</div>}
          {shown.map(c => (
            <button key={c.code} type="button" role="option" aria-selected={c.code === code} className={`sv-loc-opt${c.code === code ? ' on' : ''}`} onClick={() => setCode(c.code)}>
              <img src={`https://flagcdn.com/20x15/${c.code.toLowerCase()}.png`} alt="" loading="lazy" onError={e => { e.target.style.visibility = 'hidden'; }} />
              <span>{c.name}</span>
              <code>{c.code}</code>
            </button>
          ))}
        </div>
        <label className="sv-loc-city">
          <span>{t('servers.locationCity', 'City (optional)')}</span>
          <input className="sv-loc-input" value={city} maxLength={64} onChange={e => setCity(e.target.value)} placeholder={t('servers.locationCityPh', 'e.g. Mumbai')} />
        </label>
        {error && <div className="sv-loc-err" role="alert">{error}</div>}
      </div>
    </AnimatedModal>
  );
}
